import React, { useRef, useState, useEffect } from "react";
import "../styles/StaffProfile.css";
import API_URL from "../apiConfig";

function StaffProfile({ user, token }) {
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();

  useEffect(() => {
    const fetchProfile = async () => {
      setError("");
      try {
        const staffId = user.staff_id || user.id;
        const res = await fetch(`${API_URL}/api/staff/${staffId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error("Profile fetch failed");
        const data = await res.json();
        setProfile(data);
      } catch {
        setError("Could not fetch profile.");
      }
    };
    fetchProfile();
  }, [user, token]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file.");
      return;
    }
    setError("");
    setPreview(URL.createObjectURL(file));
  };

  const handleUpload = async () => {
    const file = fileInputRef.current.files[0];
    if (!file) return;
    setUploading(true);
    setError("");
    const staffId = user.staff_id || user.id;
    const formData = new FormData();
    formData.append("photo", file);
    try {
      const res = await fetch(`${API_URL}/api/staff/${staffId}/photo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (!res.ok) throw new Error("Upload failed");
      setPreview(null);
      fileInputRef.current.value = "";
      const refreshed = await fetch(`${API_URL}/api/staff/${staffId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(await refreshed.json());
    } catch {
      setError("Upload failed. Try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete your profile photo?")) return;
    setUploading(true);
    setError("");
    const staffId = user.staff_id || user.id;
    try {
      const res = await fetch(`${API_URL}/api/staff/${staffId}/photo`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Delete failed");
      setPreview(null);
      fileInputRef.current.value = "";
      const refreshed = await fetch(`${API_URL}/api/staff/${staffId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(await refreshed.json());
    } catch {
      setError("Delete failed. Try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="staff-profile">
      <h2 className="profile-heading">Staff Profile</h2>
      <div className="staff-profile-avatar-box">
        <img
          src={preview || profile?.profile_photo_url || "/default-avatar.png"}
          alt="Profile"
          className="staff-profile-avatar"
        />
      </div>
      <div className="staff-profile-actions">
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: "none" }}
          disabled={uploading}
        />
        <button
          className="staff-profile-btn"
          onClick={() => fileInputRef.current.click()}
          disabled={uploading}
        >
          {profile?.profile_photo_url ? "Change Photo" : "Upload Photo"}
        </button>
        {profile?.profile_photo_url && (
          <button
            className="staff-profile-btn delete"
            onClick={handleDelete}
            disabled={uploading}
          >
            Delete Photo
          </button>
        )}
      </div>
      {preview && (
        <div className="staff-profile-actions">
          <button
            className="staff-profile-btn save"
            onClick={handleUpload}
            disabled={uploading}
          >
            {uploading ? "Uploading..." : "Save Photo"}
          </button>
          <button
            className="staff-profile-btn cancel"
            onClick={() => { setPreview(null); fileInputRef.current.value = ""; }}
            disabled={uploading}
          >
            Cancel
          </button>
        </div>
      )}
      {error && <div className="staff-profile-error">{error}</div>}
      <div className="staff-profile-info">
        <div className="staff-profile-info-row">
          <span>Name:</span>
          <span>{profile?.name || "-"}</span>
        </div>
        <div className="staff-profile-info-row">
          <span>Designation:</span>
          <span>{profile?.position || "—"}</span>
        </div>
        <div className="staff-profile-info-row">
          <span>Email:</span>
          <span>{profile?.email || "-"}</span>
        </div>
      </div>
    </div>
  );
}

export default StaffProfile;